import { useState, useRef, useEffect, useLayoutEffect, useId } from 'react'
import { createPortal } from 'react-dom'
import { Info } from 'lucide-react'

export default function InfoTooltip({ text, label = 'More information', placement = 'top' }) {
  const [show, setShow] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const buttonRef = useRef(null)
  const tipRef = useRef(null)
  const timerRef = useRef(null)
  const tooltipId = useId()

  useEffect(() => () => clearTimeout(timerRef.current), [])

  useLayoutEffect(() => {
    if (!show) return
    function place() {
      const anchor = buttonRef.current?.getBoundingClientRect()
      const tip = tipRef.current?.getBoundingClientRect()
      if (!anchor || !tip) return
      let top = placement === 'bottom' ? anchor.bottom + 8 : anchor.top - tip.height - 8
      if (top < 12) top = anchor.bottom + 8
      if (top + tip.height > window.innerHeight - 12) top = anchor.top - tip.height - 8
      setPosition({
        top: Math.max(12, top),
        left: Math.max(12, Math.min(anchor.left + anchor.width / 2 - tip.width / 2, window.innerWidth - tip.width - 12)),
      })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [show, placement])

  function handleEnter() {
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setShow(true), 150)
  }

  function open() {
    clearTimeout(timerRef.current)
    setShow(true)
  }

  function handleLeave() {
    clearTimeout(timerRef.current)
    setShow(false)
  }

  return (
    <span
      className="relative inline-flex items-center ml-1 align-middle"
    >
      <button ref={buttonRef} type="button" aria-label={label} aria-describedby={show ? tooltipId : undefined}
        onMouseEnter={handleEnter} onMouseLeave={handleLeave} onFocus={open} onBlur={handleLeave} onClick={open}
        onKeyDown={event => { if (event.key === 'Escape' && show) { event.stopPropagation(); handleLeave() } }}
        className="rounded text-inkMuted opacity-60 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-1 focus-visible:outline-ink cursor-help">
        <Info size={12} aria-hidden="true" strokeWidth={2} />
      </button>
      {show && createPortal(
        <span
          ref={tipRef}
          id={tooltipId} role="tooltip"
          className="fixed z-[300] pointer-events-none rounded-lg border border-border bg-canvas px-3 py-2 text-left font-sans text-xs font-normal normal-case leading-relaxed tracking-normal text-ink shadow-lg"
          style={{ ...position, maxWidth: 'min(280px, calc(100vw - 24px))', width: 'max-content' }}
        >
          {text}
        </span>, document.body,
      )}
    </span>
  )
}
